// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { getJson, postJson } from 'Utils/http';
import type { RootState } from 'Store';
import {
  SendChatTurnStartAction, SendChatTurnSuccessAction, SendChatTurnFailureAction, SetChatProgressAction,
} from './chatbotActions';
import type {
  ChatApiResponse, ChatAppState, ChatEndpoint, ChatImageBlock, ChatMessage, ChatS3Ref,
} from './chatbotTypes';
import type { PanoViewContext } from './panoImage';

const newId = (): string => (
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
);

// A pano-vision image block is only useful on the turn it's asked. Keeping it in the RE-SENT history (and the
// persisted session) makes every subsequent request carry all prior images, which grows unboundedly and OOMs
// the server. Strip image blocks, leaving the text — so a request carries at most the CURRENT turn's image.
export const stripImageBlocks = (content: ChatMessage['content']): ChatMessage['content'] => {
  if (typeof content === 'string') return content;
  const kept = content.filter((block) => block.type !== 'image');
  if (kept.length === 1 && kept[0].type === 'text') return kept[0].text; // collapse to a plain string
  return kept.length ? kept : '';
};

// The turn's outcome. 'ok' carries the reply data + the final transcript (so the caller can persist the
// session). 'aborted' (timeout / unmount) carries no message so the hook can distinguish a timeout (shows
// a message) from an unmount cancel (silent); 'error' means the thunk already dispatched the failure
// (no reachable backend, a transport error, or an app-level `error` in a 200 body).
export type SendTurnResult =
  | { status: 'ok'; data: ChatApiResponse; messages: ChatMessage[] }
  | { status: 'error' }
  | { status: 'aborted' };

/**
 * POST one chat turn to the aw-server gdb-chat route (the only backend). Appends the user's message
 * optimistically (Start), sends the transcript + app state + turn id, and on success appends Khora's
 * reply (Success, with large-export S3 refs joined into the file download urls). A transport/app error
 * dispatches Failure here; an abort is handed back to the hook to classify.
 */
export const SendChatTurnThunk = (
  userText: string,
  params: {
    endpoint?: ChatEndpoint; appState?: ChatAppState; requestId: string; signal: AbortSignal;
    image?: ChatImageBlock; panoView?: PanoViewContext;
    // True while the order/gdb this turn was sent for is still the one loaded. Checked just before the reply
    // is committed so a turn that finishes AFTER the user switched orders never writes its transcript into
    // the newly-selected order (cross-order bleed). Omitted → always current (e.g. tests).
    isCurrent?: () => boolean;
  },
) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<SendTurnResult> => {
    // With a pano-vision image, the user turn is multimodal: a text block + the image block (the exact
    // Anthropic format the render/export/wire paths already accept). Plain text otherwise.
    const content = params.image ? [{ type: 'text' as const, text: userText }, params.image] : userText;
    const withUser: ChatMessage[] = [
      ...getState().chatbot.messages,
      { id: newId(), role: 'user', content },
    ];
    dispatch(SendChatTurnStartAction(withUser));

    // The aw-server gdb-chat endpoint is the ONLY backend — no endpoint (no resolvable order) means the
    // chat is unavailable for this view; fail the turn cleanly instead of guessing a URL.
    const { endpoint } = params;
    if (!endpoint?.url) {
      dispatch(SendChatTurnFailureAction('Khora isn’t available for this project view yet.'));
      return { status: 'error' };
    }

    // Anthropic-format messages only (drop the local `id`), plus current app state + the turn id.
    // gdb_id = the version the user has OPEN — the server chats about THAT gdb, not the newest.
    const body = {
      // Send the CURRENT turn's image (last message), but strip images from every prior turn so the request
      // stays bounded — otherwise each pano-vision turn re-sends all earlier images and OOMs the server.
      messages: withUser.map((message, index) => ({
        role: message.role,
        content: index === withUser.length - 1 ? message.content : stripImageBlocks(message.content),
      })),
      // Ride the pano-view geometry on app_state (an already-forwarded field) so aw-server needs no change;
      // Khora reads app_state.pano_view to resolve which pole the frame shows.
      app_state: params.panoView ? { ...(params.appState ?? {} as ChatAppState), pano_view: params.panoView } : params.appState,
      request_id: params.requestId,
      gdb_id: endpoint.gdbId,
    };
    const result = await postJson<ChatApiResponse>(endpoint.url, body, endpoint.token, params.signal);
    if (!result.success) {
      // A swallowed abort (timeout / unmount) surfaces as a generic failure — let the hook classify it.
      if (params.signal.aborted) return { status: 'aborted' };
      dispatch(SendChatTurnFailureAction(result.message));
      return { status: 'error' };
    }
    const { data } = result;
    if (data.error) {
      dispatch(SendChatTurnFailureAction(data.error));
      return { status: 'error' };
    }

    // A large export rides an S3 `key` (not inline b64); join it to its presigned s3_refs url so the
    // download button can fetch it. Small files keep their inline b64 unchanged.
    const refUrlByKey = new Map((data.s3_refs ?? []).map((ref) => [ref.key, ref.url]));
    const joinedFiles = (data.files ?? []).map((file) => (file.key ? { ...file, url: refUrlByKey.get(file.key) } : file));
    const assistant: ChatMessage = {
      id: newId(),
      role: 'assistant',
      content: data.answer,
      files: joinedFiles,
      charts: data.charts,
      feature_analyses: data.feature_analyses,
      widgets: data.widgets,
      cards: data.cards,
      // Keep the turn's spatial highlights on the message so the bubble can offer "Pin to map".
      highlights: data.highlights,
      // Trust: the run_sql queries behind the answer (auditable) + a groundedness check of its figures.
      provenance: data.provenance,
      grounded: data.grounded,
    };
    const finalMessages = [...withUser, assistant];
    // The user switched order/gdb while this turn was in flight → the newly-selected order now owns the
    // transcript + viewer. Committing A's reply here (and letting the caller persist it + inject its map
    // artifacts) would bleed order A into order B. Treat it like an abort: drop the reply silently.
    if (params.isCurrent && !params.isCurrent()) return { status: 'aborted' };
    dispatch(SendChatTurnSuccessAction(finalMessages));
    return { status: 'ok', data, messages: finalMessages };
  };

/**
 * GET the agent's LIVE tool status for a turn (keyed by request_id) and publish it to `progress`.
 * Best-effort — a failed/empty poll is ignored so it never affects the turn.
 */
export const PollProgressThunk = (endpoint: ChatEndpoint, requestId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    const result = await getJson<{ label?: string }>(`${endpoint.url}/progress/${requestId}`, endpoint.token);
    if (result.success && typeof result.data.label === 'string') dispatch(SetChatProgressAction(result.data.label));
  };

/**
 * POST the caller's session server-side (transcript + non-message artifacts) for this gdb version.
 * Fire-and-forget — never blocks the chat; sessions are keyed per gdb, so no loaded gdb means nothing
 * to save. NEVER localStorage (the transcript is PII-adjacent).
 */
export const SaveSessionThunk = (
  endpoint: ChatEndpoint,
  messages: ChatMessage[],
  artifacts: { session: Record<string, unknown>; s3_refs: ChatS3Ref[] },
) =>
  async (): Promise<void> => {
    if (!endpoint.url || !endpoint.gdbId) return;
    await postJson(
      `${endpoint.url}/session/${encodeURIComponent(endpoint.gdbId)}`,
      { session: { messages, ...artifacts.session }, s3_refs: artifacts.s3_refs },
      endpoint.token,
    );
  };

/**
 * GET the caller's saved session for this gdb version. Returns the transcript + the `stale` hint + the
 * non-message artifacts (with freshened s3_refs) for the caller to re-dispatch, or null on any failure
 * (missing/unreachable) so the caller keeps an empty conversation. Does NOT mutate state itself so the
 * caller can drop a stale response when the gdb/order switched mid-load.
 */
export const LoadSessionThunk = (endpoint: ChatEndpoint) =>
  async (): Promise<{ messages: ChatMessage[]; stale: boolean; session: Record<string, unknown>; s3_refs: ChatS3Ref[] } | null> => {
    if (!endpoint.url || !endpoint.gdbId) return null;
    const result = await getJson<{ session?: Record<string, unknown>; stale?: boolean; s3_refs?: ChatS3Ref[] }>(
      `${endpoint.url}/session/${encodeURIComponent(endpoint.gdbId)}`,
      endpoint.token,
    );
    if (!result.success) return null;
    const session = (result.data && result.data.session) || {};
    const rawMessages = (session as { messages?: unknown }).messages;
    return {
      messages: Array.isArray(rawMessages) ? (rawMessages as ChatMessage[]) : [],
      stale: Boolean(result.data && result.data.stale),
      session,
      s3_refs: Array.isArray(result.data?.s3_refs) ? result.data.s3_refs : [],
    };
  };
